import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Music4, Users, ExternalLink } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

const ROUTES = { fm151: "/fm151", vibe: "/vibe" };

// Live radio tile with its own now-playing poll so metadata shows even before tuning in
const RadioNowPlayingCard = ({ station }) => {
  const { current, playing, play, toggle, nowPlaying } = usePlayer();
  const isCurrent = current?.id === station.id;
  const isLive = isCurrent && playing;
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/now-playing/${station.id}`);
        if (res.ok && alive) setMeta(await res.json());
      } catch (_) {
        /* ignore */
      }
    };
    load();
    const id = setInterval(load, 20000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [station.id]);

  const np = isCurrent && nowPlaying ? nowPlaying : meta;
  const art = np?.art || station.image;

  return (
    <div
      data-testid={`radio-card-${station.id}`}
      className={`relative rounded-lg overflow-hidden border bg-jet-surface transition-colors ${
        isLive ? "border-gold shadow-[0_0_40px_rgba(212,175,55,0.18)]" : "border-gold/25 hover:border-gold/60"
      }`}
    >
      <div className="flex gap-4 p-4">
        <div className="relative w-24 h-24 md:w-28 md:h-28 shrink-0 rounded-md overflow-hidden border border-gold/30 bg-black">
          <img
            src={art}
            alt={np?.title || station.name}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`radio-art-${station.id}`}
          />
          {isLive && (
            <div className="absolute inset-x-0 bottom-0 bg-black/70 py-1 flex items-end justify-center gap-[3px] h-6">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className="w-[3px] bg-gold rounded-sm eq-bar" style={{ animationDelay: `${i * 0.12}s` }} />
              ))}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 font-condensed uppercase tracking-[0.3em] text-[10px] text-gold">
              <Music4 className="w-3 h-3" /> {station.tagline}
            </span>
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 font-condensed uppercase tracking-widest text-[10px] text-crimson-live">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-crimson-live" />
                </span>
                On Air
              </span>
            ) : (
              <span className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500">Live 24/7</span>
            )}
          </div>
          <div className="font-display text-3xl md:text-4xl text-white uppercase leading-none mt-1">{station.name}</div>
          <div className="mt-2 min-w-0" data-testid={`radio-nowplaying-${station.id}`}>
            <div className="font-condensed uppercase tracking-widest text-[9px] text-neutral-500">Now Playing</div>
            <div className="font-body text-sm text-white truncate">{np?.title || "Live stream"}</div>
            {np?.artist && <div className="font-body text-xs text-gold truncate">{np.artist}</div>}
          </div>
          <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
            <button
              data-testid={`radio-play-${station.id}`}
              onClick={() => (isCurrent ? toggle() : play(station.id))}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors"
            >
              {isLive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isLive ? "Pause" : "Listen Live"}
            </button>
            <Link
              to={ROUTES[station.id] || "/"}
              data-testid={`radio-open-${station.id}`}
              className="inline-flex items-center gap-1.5 border border-gold/40 hover:border-gold text-gold font-condensed uppercase tracking-widest text-[10px] px-3 py-2.5 rounded-full transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> Station Page
            </Link>
            {typeof np?.listeners === "number" && np.listeners > 0 && (
              <span className="ml-auto inline-flex items-center gap-1 font-condensed text-[10px] uppercase tracking-widest text-neutral-400">
                <Users className="w-3 h-3" /> {np.listeners} listening
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadioNowPlayingCard;
